import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { PhxTableCellTagPlacement } from '../../phx-table.types';
import { PhxTagSize } from '../../../tag/phx-tag.types';
import { PHX_TABLE_CELL_TAG_PLACEMENT, TAG_SIZE_BY_PLACEMENT } from '../phx-table-cell.const';

/**
 * Structured projection-based table cell. An escape hatch for cells that must host a
 * reusable/consumer component (e.g. an entity link) as a line — use
 * `phx-table-cell-string` / `phx-table-cell-tag` whenever the cell is expressible via
 * inputs; reach for this only when a projected component must occupy a slot.
 *
 * Slots (kebab attributes consumed by `<ng-content select>`, all optional):
 * `phx-custom-cell-prefix-icon` (0..N, stacks), `phx-custom-cell-primary` (0..1),
 * `phx-custom-cell-secondary` (0..N, e.g. bullet-separated segments),
 * `phx-custom-cell-tertiary` (0..N), `phx-custom-cell-tag` (0..1, positioned by
 * `phxTagPlacement`), `phx-custom-cell-inline-icon` (0..1, its own row below
 * primary/secondary/tertiary).
 *
 * Each slot's `<ng-content select>` appears EXACTLY ONCE in the template — Angular
 * assigns projected content to a slot once, statically, across the whole compiled
 * template. Repeating the same selector inside multiple `*ngSwitchCase` branches (an
 * earlier draft of this component did this) does NOT work the way it looks: only the
 * first-declared occurrence of a given selector ever receives an instance's matching
 * content, regardless of which branch is actually active at runtime — every other
 * occurrence silently renders nothing. Placement is therefore driven entirely by CSS
 * (`grid-template-areas`, switched per `phxTagPlacement` host class in the .scss), not
 * by moving `<ng-content>` between branches.
 *
 * The component owns layout only — projected `phx-text` supplies its own
 * `phxTruncate`; secondary/tertiary rows demote `phx-text`'s default
 * size/intent (medium/standard) to small/subdued via CSS (see .scss) — an explicit,
 * non-default `phxSize`/`phxTextIntent` on the projected text is left untouched.
 *
 * The projected `phx-custom-cell-tag` slot's `<phx-tag>` is NOT auto-sized by this
 * component — a wrapping component cannot reliably force a re-render on a
 * content-projected OnPush component it did not instantiate. Instead, this component
 * exposes `resolvedTagSize` (the same per-placement size `phx-table-cell-tag` uses
 * internally); bind it explicitly on the projected tag via a template reference
 * variable so it stays a real, live Angular binding:
 * `<phx-table-cell-custom #cell="phxTableCellCustom" phxTagPlacement="above-primary">
 *    <phx-tag phx-custom-cell-tag [phxSize]="cell.resolvedTagSize">...</phx-tag>
 *  </phx-table-cell-custom>`
 */
@Component({
  selector: 'phx-table-cell-custom',
  exportAs: 'phxTableCellCustom',
  templateUrl: './phx-table-cell-custom.component.html',
  styleUrls: ['./phx-table-cell-custom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'phx-table-cell-custom',
    '[class.phx-table-cell-custom-tag-only]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.TAG_ONLY`,
    '[class.phx-table-cell-custom-as-primary]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.AS_PRIMARY`,
    '[class.phx-table-cell-custom-above-primary]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.ABOVE_PRIMARY`,
    '[class.phx-table-cell-custom-below-primary]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.BELOW_PRIMARY`,
    '[class.phx-table-cell-custom-right-of-secondary]': `phxTagPlacement === PHX_TABLE_CELL_TAG_PLACEMENT.RIGHT_OF_SECONDARY`,
    // right-of-primary is the default grid shape defined on the host — no extra class needed.
  },
})
export class PhxTableCellCustomComponent {
  /** Where the projected `phx-custom-cell-tag` slot renders relative to the text rows. */
  @Input() phxTagPlacement: PhxTableCellTagPlacement = PHX_TABLE_CELL_TAG_PLACEMENT.RIGHT_OF_PRIMARY;

  public readonly PHX_TABLE_CELL_TAG_PLACEMENT = PHX_TABLE_CELL_TAG_PLACEMENT;

  /** The `phx-tag` size matching `phx-table-cell-tag`'s own sizing for this placement — bind explicitly on the projected tag. */
  public get resolvedTagSize(): PhxTagSize {
    return TAG_SIZE_BY_PLACEMENT[this.phxTagPlacement] ?? 'small';
  }
}
